import { useCallback, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface LoanNumberCellProps {
  loanNumber: string | null | undefined;
  /** Optional extra classes for the copy button. */
  className?: string;
  /** Let a long number wrap onto more lines instead of being cut off. */
  wrap?: boolean;
}

/**
 * Clickable loan number that copies to clipboard. Click is `stopPropagation`ed
 * so it never opens the loan detail drawer. Shows a green check briefly on
 * success and a toast with the copied value.
 */
export function LoanNumberCell({ loanNumber, className, wrap = false }: LoanNumberCellProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!loanNumber) {
      return;
    }
    try {
      await navigator.clipboard.writeText(loanNumber);
      setCopied(true);
      toast.success(`Copied ${loanNumber}`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  }, [loanNumber]);

  if (!loanNumber) {
    return <span className={cn('font-mono text-[11px] text-muted-foreground', className)}>—</span>;
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      title="Click to copy"
      aria-label={`Copy loan number ${loanNumber}`}
      className={cn(
        'inline-flex items-center gap-1 font-mono text-[11px] text-muted-foreground',
        'hover:text-primary hover:underline group cursor-pointer min-w-0 rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2',
        wrap && 'max-w-full text-left',
        className
      )}
    >
      <span className={wrap ? 'min-w-0 break-all' : 'truncate'}>{loanNumber}</span>
      {copied ? (
        <Check className="h-3 w-3 text-success flex-shrink-0" aria-hidden />
      ) : (
        <Copy className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity flex-shrink-0" aria-hidden />
      )}
    </button>
  );
}
